import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { PetDetailsForm } from '@/components/pet-details-form'
import { useGlobalPublicConfig } from '@/shared/hooks/use-global-config'
import { usePetPricing } from '@/shared/hooks/use-pet-pricing'
import { Button } from '@/shared/ui/button'

import { ClientProfile } from '@/hooks/use-client-profile'
import { useUser } from '@/shared/auth'
import { useMemo } from 'react'
import { BookingFormData } from '../../types/booking.types'

interface PetInformationStepProps {
    form: UseFormReturn<BookingFormData>
    clientProfile: ClientProfile | undefined
    onAddPet: () => void
    onRemovePet: (index: number) => void
}

export function PetInformationStep({ form, clientProfile, onAddPet, onRemovePet }: PetInformationStepProps) {
    const { t } = useTranslation()
    const user = useUser()
    const { data: globalConfig } = useGlobalPublicConfig()
    const pets = form.watch('pets') || []
    const { getTotalPetsPrice } = usePetPricing()
    // Convert PetFormData[] to Pet[] format for pricing calculation
    const petsForPricing = pets.map(pet => ({
        name: pet.name,
        breed: pet.breed,
        age: pet.age,
        size: pet.size,
        weight: pet.weight,
        sex: pet.sex,
        isNeutered: pet.isNeutered,
        personality: pet.personality || '',
    }))
    const totalPetsPrice = getTotalPetsPrice(petsForPricing, clientProfile?.classification)
    const allowAddPet = useMemo(() => {
        return user.isStaff || pets.length < 3
    }, [user, pets])

    return (
        <div className='flex flex-col space-y-4'>
            <div className=''>
                {pets.map((pet, index) => (
                    <PetDetailsForm
                        key={pet.id ?? `pet-${index}`}
                        form={form}
                        petIndex={index}
                        onRemove={() => onRemovePet(index)}
                        petPrice={totalPetsPrice / pets.length}
                        canRemove={pets.length > 1}
                    />
                ))}
                {allowAddPet && (
                    <Button type='button' variant='outline' className='w-full' onClick={onAddPet}>
                        {t('booking.step1.addPet', 'Añadir otra mascota')}
                    </Button>
                )}
                {pets.length >= 3 && (
                    <div className='mt-4 border-l-4 border-yellow-500 bg-yellow-100 p-4 text-yellow-700'>
                        <p>
                            {t(
                                'booking.step1.maxPetsMessage',
                                'Para más de 3 perros o 3 perros de diferente tamaño, llama al teléfono: {{phoneNumber}}',
                                {
                                    phoneNumber: globalConfig?.phoneNumber || '',
                                },
                            )}
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}
